import { useEffect, useRef, useState } from "react"
import { Autocomplete, Box, Button, ButtonGroup, Chip, Tab, TextField, Typography } from "@mui/material"
import { TabContext, TabPanel } from "@mui/lab"
import { useParams } from "react-router-dom"
import { useSnackbar } from "notistack"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { EnforceTabList } from "lib/@components/enforceTabList"
import { TreeToolbar } from "slot-definitions"
import { CopDocumentsTabActions } from "./cop-slot-definitions"
import { useDialog } from "lib/@hooks/useDialog"
import { useSave } from "lib/@hooks/use-save"
import { useReferenceState } from "lib/@hooks/use-reference"
import { Bound } from "lib/@components/binding/Bound"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { setFromValueParam } from "lib/setFromEvent"
import { getAllCodesOfPractice } from "./controller/get-all-codes-of-practice.controller"
import { getCopResolutionsByGroup } from "./controller/get-cop-resolutions-by-group.controller"
import { saveCodeOfPractice } from "./controller/save-code-of-practice.controller"
import { triggerCopScan } from "./controller/trigger-cop-scan.controller"
import { stopCopScan } from "./controller/stop-cop-scan.controller"
import { generateCopSchedules } from "./controller/generate-cop-schedules.controller"
import { CopGroupDocumentsTab } from "./cop-group-documents-tab"
import { CopResolutionList } from "./cop-resolution-list"
import { CopSampleScanDialog } from "./cop-sample-scan-dialog"
import { CopScanAuthorityDialog } from "./cop-scan-authority-dialog"
import { CopScanProgress } from "./cop-scan-progress"
import { CopClientAssignmentTab } from "./cop-client-assignment-tab"
import { COUNTRIES } from "./countries"

export function CopGroupDetailPage() {
    const { groupId } = useParams()
    const id = decodeURIComponent(groupId)
    const { enqueueSnackbar } = useSnackbar()
    const groups = getAllCodesOfPractice.useResults()
    const group = groups?.find((g) => g._id === id)
    const openSampleScan = useDialog(<CopSampleScanDialog />, { maxWidth: "md", fullWidth: true })
    const openScanAuthorityDialog = useDialog(<CopScanAuthorityDialog />, { maxWidth: "xs", fullWidth: true })
    const emptyGroup = useRef({})
    const target = group ?? emptyGroup.current
    const onChange = useSave({ save: handleSave, target, wait: 1000 })
    const [tab, setTab] = useReferenceState("cop-group-tab", "documents")

    const [generating, setGenerating] = useState(false)
    const resolutions = getCopResolutionsByGroup.useResults({ codeOfPracticeId: id })
    const resolutionCount = resolutions?.length ?? 0
    // null = never visited the Resolutions tab (no highlight); otherwise the count at last visit
    const [lastSeenCount, setLastSeenCount] = useReferenceState(`cop-resolutions-seen-${id}`, null)
    const newResolutions = lastSeenCount !== null ? Math.max(0, resolutionCount - lastSeenCount) : 0

    // Optimistic status overrides the server value until the invalidation arrives.
    // null = no override; "scanning" = just started; "cancelled" = just stopped.
    const [optimisticStatus, setOptimisticStatus] = useState(null)
    const effectiveStatus = optimisticStatus ?? group?.scanStatus
    const isScanning = effectiveStatus === "scanning"
    const isResumable =
        !isScanning &&
        (effectiveStatus === "error" || effectiveStatus === "cancelled") &&
        (group?.scanAssessed ?? 0) > 0

    useEffect(clearOptimisticOnServerUpdate, [group?.scanStatus])
    useEffect(markResolutionsAsSeen, [tab, resolutionCount])

    if (!groups) return <LoadingScreen />
    if (!group) {
        return (
            <PageBreadcrumbContainer
                title="Not Found"
                parentCrumbs={[{ label: "Codes of Practice", to: "/app/sfg20admin" }]}
            >
                <Box sx={{ p: 3 }}>
                    <Typography>Code of Practice group not found.</Typography>
                </Box>
            </PageBreadcrumbContainer>
        )
    }

    return (
        <PageBreadcrumbContainer
            title={group.name}
            parentCrumbs={[{ label: "Codes of Practice", to: "/app/sfg20admin" }]}
        >
            <TreeToolbar.Plug deps={[generating, resolutionCount, isScanning, isResumable]}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <If value={isScanning}>
                        <ButtonGroup disableElevation>
                            <Button
                                variant="outlined"
                                startIcon={<Iconify icon="mdi:test-tube" />}
                                onClick={handleSampleScan}
                            >
                                Scan Sample
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<Iconify icon="mdi:stop-circle-outline" />}
                                onClick={handleStop}
                            >
                                Stop Scan
                            </Button>
                            <Button variant="outlined" startIcon={<Iconify icon="mdi:magnify-scan" />} disabled>
                                Scanning…
                            </Button>
                        </ButtonGroup>
                        <Else>
                            <If value={isResumable}>
                                <ButtonGroup disableElevation>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Iconify icon="mdi:test-tube" />}
                                        onClick={handleSampleScan}
                                    >
                                        Scan Sample
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<Iconify icon="mdi:play-circle-outline" />}
                                        onClick={handleResume}
                                    >
                                        Resume ({(group.scanAssessed ?? 0).toLocaleString()}/
                                        {(group.scanTotal ?? "?").toLocaleString()})
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Iconify icon="mdi:refresh" />}
                                        onClick={handleScan}
                                    >
                                        Restart
                                    </Button>
                                </ButtonGroup>
                                <Else>
                                    <ButtonGroup disableElevation>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Iconify icon="mdi:test-tube" />}
                                            onClick={handleSampleScan}
                                        >
                                            Scan Sample
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Iconify icon="mdi:magnify-scan" />}
                                            onClick={handleScan}
                                        >
                                            Scan All Schedules
                                        </Button>
                                    </ButtonGroup>
                                </Else>
                            </If>
                        </Else>
                    </If>
                    <Button
                        variant="contained"
                        startIcon={<Iconify icon="mdi:cog-sync-outline" />}
                        onClick={handleGenerate}
                        disabled={generating || resolutionCount === 0}
                    >
                        <If value={generating}>
                            Building…
                            <Else>Build CoP Schedules</Else>
                        </If>
                    </Button>
                </Box>
            </TreeToolbar.Plug>

            <Box sx={{ px: 2, pt: 2, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <If value={isScanning}>
                    <CopScanProgress group={group} />
                </If>
                <If value={group.schedulesGeneratedAt}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                        CoP schedules built: {group.schedulesGeneratedCount ?? 0} schedule
                        {(group.schedulesGeneratedCount ?? 0) !== 1 ? "s" : ""} &mdash;{" "}
                        {new Date(group.schedulesGeneratedAt).toLocaleString()}
                    </Typography>
                </If>

                <Bound target={target} onChange={onChange} country={group.country}>
                    <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                        <BoundTextField
                            field="name"
                            label="Name"
                            fullWidth
                            size="small"
                            yup="string.trim.required"
                            sideEffects={true}
                        />
                        <CountryPicker />
                    </Box>

                    <TabContext value={tab}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: 1,
                                borderColor: "divider",
                            }}
                        >
                            <EnforceTabList onChange={setFromValueParam(handleTabChange)}>
                                <Tab label="Documents" value="documents" />
                                <Tab
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            Resolutions
                                            <If value={resolutionCount > 0}>
                                                <Chip
                                                    label={resolutionCount}
                                                    size="small"
                                                    color={newResolutions > 0 ? "warning" : "default"}
                                                    variant="filled"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: "0.7rem",
                                                        "& .MuiChip-label": { px: 0.75 },
                                                    }}
                                                />
                                            </If>
                                        </Box>
                                    }
                                    value="resolutions"
                                />
                                <Tab label="Clients" value="clients" />
                                <Tab label="Settings" value="settings" />
                            </EnforceTabList>
                            <Box sx={{ ml: "auto", pr: 1, flexShrink: 0 }}>
                                <CopDocumentsTabActions.Slot />
                            </Box>
                        </Box>
                        <TabPanel
                            value="documents"
                            sx={{
                                p: 0,
                                pt: 1,
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                "&[hidden]": { display: "none" },
                            }}
                        >
                            <CopGroupDocumentsTab codeOfPracticeId={id} />
                        </TabPanel>
                        <TabPanel
                            value="resolutions"
                            sx={{ p: 0, pt: 0, flex: 1, overflowY: "auto", "&[hidden]": { display: "none" } }}
                        >
                            <CopResolutionList codeOfPracticeId={id} />
                        </TabPanel>
                        <TabPanel
                            value="clients"
                            sx={{ p: 0, pt: 1, flex: 1, overflowY: "auto", "&[hidden]": { display: "none" } }}
                        >
                            <CopClientAssignmentTab codeOfPracticeId={id} />
                        </TabPanel>
                        <TabPanel
                            value="settings"
                            sx={{ p: 0, pt: 2, flex: 1, overflowY: "auto", "&[hidden]": { display: "none" } }}
                        >
                            <Box>
                                <BoundTextField
                                    field="description"
                                    label="Description"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                />
                            </Box>
                        </TabPanel>
                    </TabContext>
                </Bound>
            </Box>
        </PageBreadcrumbContainer>
    )

    async function handleSave(data) {
        await saveCodeOfPractice({
            id,
            name: data.name,
            description: data.description,
            country: data.country,
        })
    }

    async function handleScan() {
        const authorityFilter = await openScanAuthorityDialog()
        if (!authorityFilter) return
        setOptimisticStatus("scanning")
        await triggerCopScan({ codeOfPracticeId: id, authorityFilter, resume: false })
        enqueueSnackbar("Scan queued — this may take several minutes to complete", { variant: "info" })
    }

    async function handleResume() {
        setOptimisticStatus("scanning")
        await triggerCopScan({ codeOfPracticeId: id, resume: true })
        enqueueSnackbar("Resuming scan from where it left off…", { variant: "info" })
    }

    async function handleStop() {
        setOptimisticStatus("cancelled")
        await stopCopScan({ codeOfPracticeId: id })
        enqueueSnackbar("Scan stopped", { variant: "info" })
    }

    function clearOptimisticOnServerUpdate() {
        setOptimisticStatus(null)
    }

    function handleTabChange(value) {
        setTab(value)
        if (value === "resolutions") {
            setLastSeenCount(resolutionCount)
        }
    }

    function markResolutionsAsSeen() {
        if (tab === "resolutions") {
            setLastSeenCount(resolutionCount)
        }
    }

    async function handleSampleScan() {
        await openSampleScan({ codeOfPracticeId: id })
    }

    async function handleGenerate() {
        setGenerating(true)
        try {
            const result = await generateCopSchedules({ codeOfPracticeId: id })
            enqueueSnackbar(
                `Built ${result.generated} CoP schedule${result.generated !== 1 ? "s" : ""}` +
                    (result.deletedStale ? `, removed ${result.deletedStale} stale` : ""),
                { variant: "success" }
            )
        } catch (e) {
            enqueueSnackbar(`Build failed: ${e.message}`, { variant: "error" })
        } finally {
            setGenerating(false)
        }
    }
}

function CountryPicker() {
    const { target, refresh } = useBoundContext()

    return (
        <Autocomplete
            value={target.country || null}
            options={COUNTRIES}
            onChange={handleChange}
            renderInput={(params) => <TextField {...params} label="Country" size="small" />}
            sx={{ minWidth: 250 }}
            size="small"
        />
    )

    function handleChange(_, value) {
        target.country = value ?? ""
        refresh()
    }
}
